import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { groupBy } from 'lodash';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import type { DeepUnknown } from '@/types/deep-unknown';
import { siteLabel } from '@/utils/format/customer';

import { SiteSchema } from '../entities/site';
import { WorkstreamSchema } from '../entities/workstream';

export namespace SitesQuery {
  export const key = () => ['project-management', 'sites'];

  export const OutputSchema = z.object({
    sites: z.array(
      SiteSchema.pick({ id: true, name: true }).extend({
        workstreams: z.array(
          WorkstreamSchema.pick({
            id: true,
            name: true,
            progress: true,
            endDate: true,
          }),
        ),
      }),
    ),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    sitesData: FeApi.SiteListBasicViewSite,
    workstreamsData: FeApi.ListWorkstreamsViewOutput[],
  ): DeepUnknown<Output> {
    const workstreamsDataPerSite = groupBy(
      workstreamsData,
      (ws) => ws.project.accure_id,
    );

    return {
      sites: sitesData.sites.map((site) => ({
        id: site.accure_id,
        name: siteLabel(site.full_frontend_id),
        workstreams:
          workstreamsDataPerSite[site.accure_id]?.map((ws) => ({
            id: ws.uuid,
            name: ws.name,
            progress: ws.progress / 100,
            startDate: new Date(ws.start_date),
            endDate: new Date(ws.end_date),
          })) ?? [],
      })),
    };
  }

  function useQueryFn() {
    const sitesList = useInjectFeApiContext(
      FeApi.customerInfosV1SitesListBasicRetrieve,
    );
    const workstreamsList = useInjectFeApiContext(
      FeApi.tasksV1WorkstreamsListList,
    );

    return async function queryFn() {
      const [sitesResult, workstreamsResult] = await Promise.all([
        ok(sitesList({})),
        ok(workstreamsList({})),
      ]);

      const output = await OutputSchema.parseAsync(
        outputMapper(sitesResult, workstreamsResult),
      );

      return output;
    };
  }

  export function useQuery() {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(),
      queryFn: () => queryFn(),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(),
        queryFn: () => queryFn(),
      });
    };
  }
}
